import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { TableModule } from 'primeng/table';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectButtonModule } from 'primeng/selectbutton';
import { catchError, throwError } from 'rxjs';
import uuid4 from 'uuid4';
import { CrosselingItem, CrosselingScope } from '../../../../../model/Crosseling/CrosselingItem';
import { HttpResponse } from '../../../../../model/HttpResponse';
import { Modalidad } from '../../../../../model/Modalidad';
import { AcademyAdministratorOperation } from '../../../../../model/administrator/AcademyAdministratorOperation';
import { AcademyAdministratorType } from '../../../../../model/administrator/AcademyAdministratorType';
import { AdministratorEntity } from '../../../../../model/administrator/AdministratorEntity';
import { Competition, IdObject } from '../../../../../model/competitions/Competition';
import { HockeyCategory } from '../../../../../model/competitions/HockeyCategory';
import { CategoryLevel } from '../../../../../model/competitions/Level';
import { CompetitionType } from '../../../../../model/competitions/competitionType';
import { HockeyPaymentModel } from '../../../../../model/enum/HockeyPaymentModel';
import { CompetitionService } from '../../../../../services/competition.service';
import { LoadingService } from '../../../../../services/loading.service';

interface CategoryWithCutoffDate {
  category: HockeyCategory;
  cutoffDate: string;
}

@Component({
  selector: 'app-hockey-competition-form',
  standalone: true,
  imports: [
    InputTextModule,
    SelectButtonModule,
    CommonModule,
    DialogModule,
    ReactiveFormsModule,
    FormsModule,
    MultiSelectModule,
    InputNumberModule,
    InputTextareaModule,
    TableModule,
    ConfirmDialogModule,
    ButtonModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './hockey-competition-form.component.html',
  styleUrl: './hockey-competition-form.component.scss',
})
export class HockeyCompetitionFormComponent implements OnInit {
  validateForm!: FormGroup;
  showForm: boolean = false;
  dialogHeader: string = '';

  @Input()
  isUpdateForm: boolean = false;

  @Input()
  competitionToUpdate!: Competition;

  @Input()
  levels!: CategoryLevel[];

  @Input()
  categories!: HockeyCategory[];

  @Output()
  onCompetitionCreated: EventEmitter<Competition> =
    new EventEmitter<Competition>();

  @Output()
  onFormClosed: EventEmitter<boolean> = new EventEmitter<boolean>();

  competitionTypes: { label: string; value: CompetitionType }[] = [
    { label: 'Vinculados', value: CompetitionType.MEMBERS },
    { label: 'Externos', value: CompetitionType.EXTERNAL },
    { label: 'Vinculados y Externos', value: CompetitionType.MEMBERS_AND_EXTERNAL },
  ];

  hockeyLevels: CategoryLevel[] = [];
  categoriesWithCutoff: CategoryWithCutoffDate[] = [];
  selectedCategories: HockeyCategory[] = [];
  availableCategories: HockeyCategory[] = [];
  
  stateOptions: any[] = [
    { label: 'No', value: false },
    { label: 'Si', value: true },
  ];

  crosselingItems: CrosselingItem[] = [];
  showCrosselingDialog: boolean = false;
  editingCrosselingItem: CrosselingItem | null = null;
  editingIndex: number = -1;
  scopeOptions = [
    { label: 'Por Atleta', value: CrosselingScope.PER_ATHLETE },
    { label: 'Por Equipo', value: CrosselingScope.PER_TEAM }
  ];

  paymentModelOptions = [
    { label: 'Por Equipo', value: HockeyPaymentModel.PER_TEAM },
    { label: 'Por Atleta', value: HockeyPaymentModel.PER_ATHLETE }
  ];

  constructor(
    private fb: FormBuilder,
    private loadingService: LoadingService,
    private competitionService: CompetitionService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.filterHockeyLevels();
    if (this.isUpdateForm) {
      this.getEditForm(this.competitionToUpdate);
    } else {
      this.getFormNew();
    }
    this.showForm = true;
  }

  filterHockeyLevels(): void {
    // Filtrar niveles específicos para Hockey
    this.hockeyLevels = this.levels.filter(level => 
      this.categories.some(category => category.idCategoryLevel === level.id)
    );
  }

  onLevelSelected(event: any): void {
    const selectedLevelIds: string[] = event.value;
    this.selectedCategories = [];
    this.categoriesWithCutoff = [];
    
    // Filtrar categorías basadas en los niveles seleccionados
    this.availableCategories = this.categories.filter(category => 
      selectedLevelIds.some(levelId => levelId === category.idCategoryLevel)
    );
    
    this.validateForm.get('categories')?.setValue([]);
  }

  onCategorySelected(event: any): void {
    const selectedCategoryIds: string[] = event.value;
    this.selectedCategories = this.categories.filter(category => 
      selectedCategoryIds.includes(category.id)
    );
    this.updateCategoriesWithCutoff();
  }

  updateCategoriesWithCutoff(): void {
    // Mantener fechas existentes y añadir nuevas categorías
    const existingDates = new Map(
      this.categoriesWithCutoff.map(item => [item.category.id, item.cutoffDate])
    );

    this.categoriesWithCutoff = this.selectedCategories.map(category => ({
      category,
      cutoffDate: existingDates.get(category.id) || ''
    }));
  }

  updateCutoffDate(categoryId: string, date: string): void {
    const item = this.categoriesWithCutoff.find(item => item.category.id === categoryId);
    if (item) {
      item.cutoffDate = date;
    }
  }

  getFormNew(): void {
    this.validateForm = this.fb.group({
      type: [null, [Validators.required]],
      name: [null, [Validators.required]],
      startDate: [null, [Validators.required]],
      endDate: [null, [Validators.required]],
      location: [null, [Validators.required]],
      levels: [null, [Validators.required]],
      categories: [null, [Validators.required]],
      urlImage: [null],
      digitalPaymentAvailable: [true, [Validators.required]],
      manualPaymentAvailable: [false, [Validators.required]],
      paymentModel: [HockeyPaymentModel.PER_TEAM, [Validators.required]],
      hasCrosselling: [false, [Validators.required]],
      isOpenToRegister: [false, [Validators.required]],
      isPublished: [false, [Validators.required]],
    });
    this.dialogHeader = 'Nueva competencia de Hockey';
    this.isUpdateForm = false;
    this.showForm = true;
  }

  getEditForm(item: Competition): void {
    // Primero cargar las categorías seleccionadas
    this.selectedCategories = this.categories.filter(category =>
      item.categories?.some(compCategory => compCategory.id === category.id)
    );

    // Luego obtener los niveles seleccionados y configurar categorías disponibles
    const selectedLevels = this.hockeyLevels.filter(level =>
      this.selectedCategories.some(category => category.idCategoryLevel === level.id)
    );
    
    this.availableCategories = this.categories.filter(category => 
      selectedLevels.some(level => level.id === category.idCategoryLevel)
    );

    // Cargar fechas de corte
    const customData = item.customData as any;
    const categoryCutoffDates = customData?.categoryCutoffDates || {};
    this.categoriesWithCutoff = this.selectedCategories.map(category => ({
      category,
      cutoffDate: categoryCutoffDates[category.id] || item.baseDateToCalculateAge || ''
    }));
    
    this.validateForm = this.fb.group({
      id: [item.id, [Validators.required]],
      type: [item.type, [Validators.required]],
      name: [item.name, [Validators.required]],
      startDate: [item.startDate, [Validators.required]],
      endDate: [item.endDate, [Validators.required]],
      location: [item.location, [Validators.required]],
      levels: [selectedLevels.map(level => level.id), [Validators.required]],
      categories: [this.selectedCategories.map(category => category.id), [Validators.required]],
      urlImage: [item.urlImage || ''],
      digitalPaymentAvailable: [item.payment?.digitalPaymentAvailable || true, [Validators.required]],
      manualPaymentAvailable: [item.payment?.manualPaymentAvailable || false, [Validators.required]],
      paymentModel: [item.customData?.['paymentModel'] || HockeyPaymentModel.PER_TEAM, [Validators.required]],
      hasCrosselling: [(item as any).hasCrosselling || false, [Validators.required]],
      isOpenToRegister: [item.isOpenToRegister, [Validators.required]],
      isPublished: [item.isPublished, [Validators.required]],
    });

    if (item.customData?.crosselingItems) {
      this.crosselingItems = [...item.customData.crosselingItems];
    }

    this.dialogHeader = `Editar competencia ${item.name}`;
    this.isUpdateForm = true;
    this.showForm = true;
  }


  saveCompetition(): void {
    if (this.validateForm.valid && this.validateCategoryCutoffDates()) {
      this.loadingService.showLoading();
      const competition = this.buildCompetition(this.validateForm.value);
      const entity: AdministratorEntity = {
        operation: AcademyAdministratorOperation.CREATE,
        type: AcademyAdministratorType.COMPETIIONS,
        payload: competition,
      };

      this.competitionService
        .sendRequest(entity)
        .pipe(catchError(this.handleError.bind(this)))
        .subscribe((res: HttpResponse) => {
          if (res.code === 200) {
            this.messageService.add({
              severity: 'success',
              summary: 'Éxito',
              detail: 'Competencia de Hockey creada correctamente',
            });
            this.showForm = false;
            this.onCompetitionCreated.emit(entity.payload);
            this.loadingService.hideLoading();
          }
        });
    }
  }

  updateCompetition(): void {
    if (this.validateForm.valid && this.validateCategoryCutoffDates()) {
      this.confirmationService.confirm({
        message: '¿Estás seguro de actualizar la competencia?',
        header: 'Confirmar',
        acceptLabel: 'Sí',
        rejectLabel: 'No',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.loadingService.showLoading();
          const competition = this.buildCompetition(this.validateForm.value);
          const entity: AdministratorEntity = {
            operation: AcademyAdministratorOperation.UPDATE,
            type: AcademyAdministratorType.COMPETIIONS,
            payload: competition,
          };

          this.competitionService
            .sendRequest(entity)
            .pipe(catchError(this.handleError.bind(this)))
            .subscribe((res: HttpResponse) => {
              if (res.code === 200) {
                this.messageService.add({
                  severity: 'success',
                  summary: 'Éxito',
                  detail: 'Competencia actualizada correctamente',
                });
                this.showForm = false;
                this.onCompetitionCreated.emit(entity.payload);
                this.loadingService.hideLoading();
              }
            });
        },
      });
    }
  }

  validateCategoryCutoffDates(): boolean {
    const invalidDates = this.categoriesWithCutoff.filter(item => !item.cutoffDate);
    if (invalidDates.length > 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Todas las categorías deben tener una fecha de corte definida',
      });
      return false;
    }
    return true;
  }

  buildCompetition(formCompetition: any): Competition {
    // Construir objeto de fechas de corte por categoría
    const categoryCutoffDates: { [categoryId: string]: string } = {};
    this.categoriesWithCutoff.forEach(item => {
      categoryCutoffDates[item.category.id] = item.cutoffDate;
    });

    // Preservar customData existente en modo edición
    const existingCustomData = this.isUpdateForm && this.competitionToUpdate?.customData
      ? { ...this.competitionToUpdate.customData as any }
      : {};

    const competition: Competition = {
      id: this.isUpdateForm ? formCompetition.id : uuid4(),
      type: formCompetition.type,
      name: formCompetition.name,
      startDate: formCompetition.startDate,
      endDate: formCompetition.endDate,
      location: formCompetition.location,
      baseDateToCalculateAge: this.categoriesWithCutoff[0]?.cutoffDate || '', // Por compatibilidad
      categories: this.selectedCategories.map((category: HockeyCategory) => ({ id: category.id })),
      registerDates: {
        ordinary: { start: '', end: '' },
        extraordinary: { start: '', end: '' },
      },
      registerAmounts: {
        ordinary: { athlete: 0, club: 0 },
        extraordinary: { athlete: 0, club: 0 },
      },
      urlResolution: '',
      urlImage: formCompetition.urlImage || '',
      isOpenToRegister: formCompetition.isOpenToRegister,
      isPublished: formCompetition.isPublished,
      hasCrosselling: formCompetition.hasCrosselling || false,
      modality: Modalidad.HOCKEY,
      urlToRegister: '',
      payment: {
        digitalPaymentAvailable: formCompetition.digitalPaymentAvailable,
        manualPaymentAvailable: formCompetition.manualPaymentAvailable,
      },
      customData: {
        ...existingCustomData,
        categoryCutoffDates,
        isHockeyCompetition: true,
        paymentModel: formCompetition.paymentModel,
        crosselingItems: formCompetition.hasCrosselling && this.crosselingItems.length > 0 ? this.crosselingItems : undefined
      } as any
    };
    return competition;
  }

  addCrosselingItem() {
    this.editingCrosselingItem = {
      id: '',
      name: '',
      description: '',
      amount: 0,
      urlImage: '',
      scope: CrosselingScope.PER_ATHLETE
    };
    this.editingIndex = -1;
    this.showCrosselingDialog = true;
  }

  editCrosselingItem(item: CrosselingItem, index: number) {
    this.editingCrosselingItem = { ...item };
    this.editingIndex = index;
    this.showCrosselingDialog = true;
  }

  saveCrosselingItem() {
    if (this.editingCrosselingItem) {
      if (this.editingIndex === -1) {
        this.editingCrosselingItem.id = uuid4();
        this.crosselingItems.push(this.editingCrosselingItem);
      } else {
        this.crosselingItems[this.editingIndex] = this.editingCrosselingItem;
      }
      this.closeCrosselingDialog();
    }
  }

  deleteCrosselingItem(index: number) {
    this.confirmationService.confirm({
      message: '¿Estás seguro de eliminar este item de venta cruzada?',
      header: 'Confirmar',
      acceptLabel: 'Sí',
      rejectLabel: 'No',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.crosselingItems.splice(index, 1);
      }
    });
  }

  closeCrosselingDialog() {
    this.showCrosselingDialog = false;
    this.editingCrosselingItem = null;
    this.editingIndex = -1;
  }

  closeDialog(): void {
    this.validateForm.reset();
    this.categoriesWithCutoff = [];
    this.selectedCategories = [];
    this.crosselingItems = [];
    this.onFormClosed.emit(true);
    this.showForm = false;
  }

  handleError(error: HttpErrorResponse) {
    this.loadingService.hideLoading();
    if (error.status === 422) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se puede procesar esta competencia. Puede que ya exista una competencia con el mismo nombre',
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Ha ocurrido un error inesperado, por favor intenta de nuevo',
      });
      this.validateForm.reset();
    }
    return throwError(
      () => new Error('Something bad happened; please try again later.')
    );
  }
}